import React, { useEffect, useMemo, useState } from "react";
import { Spinner, Dropdown, Row, Col, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import AdvanceTable from "components/common/advance-table/AdvanceTable";
import AdvanceTableProvider from "providers/AdvanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import IconButton from "components/common/IconButton";
import CustomPaginationFooter from "components/common/CustomPaginationFooter";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useAffiliations from "hooks/userManagement/useAffiliations";
import AffiliationForm from "./AffiliationForm";
import EditAffiliationForm from "./EditAffiliationTable";
import ViewAffiliationForm from "./ViewAffiliationTable";
import { toast } from "react-toastify";
import { Select, Input } from "antd";

const AffiliationAssignmentsTable = () => {
  const navigate = useNavigate();
  const { affiliations, fetchAffiliations, deleteAffiliation, loading } = useAffiliations();

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [affiliationToDelete, setAffiliationToDelete] = useState(null);
  const [affiliationToEdit, setAffiliationToEdit] = useState(null);
  const [affiliationToView, setAffiliationToView] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("ALL");

  useEffect(() => {
    fetchAffiliations();
  }, [fetchAffiliations]);

  const editAffiliation = (id) => { setAffiliationToEdit(id); setShowEditModal(true); };
  const viewAffiliation = (id) => { setAffiliationToView(id); setShowViewModal(true); };
  const confirmRemove = (id) => { setAffiliationToDelete(id); setShowDeleteModal(true); };

  const handleEdit = (updatedAffiliation) => {
    setShowEditModal(false);
    setAffiliationToEdit(null);
    fetchAffiliations();
  };

  const handleRemove = async () => {
    if (!affiliationToDelete) return;
    try {
      await deleteAffiliation(affiliationToDelete);
      toast.success("Affiliation deleted");
      fetchAffiliations();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete affiliation");
    } finally {
      setShowDeleteModal(false);
      setAffiliationToDelete(null);
    }
  };

  const handleAssignAdded = async () => {
    setShowAssignModal(false);
    fetchAffiliations();
  };

  // Filter affiliations by search and category
  const filteredAffiliations = useMemo(() => {
    return affiliations
      .filter((a) =>
        `${a.displayName || ""} ${a.code || ""}`.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .filter((a) => selectedCategory === "ALL" || a.category === selectedCategory);
  }, [affiliations, searchTerm, selectedCategory]);

  // Generate unique category options
  const categoryOptions = useMemo(() => {
    const unique = [...new Set(affiliations.map((a) => a.category).filter(Boolean))];
    return [
      { label: "🌍 All Categories", value: "ALL" },
      ...unique.map((c) => ({ label: `📂 ${c}`, value: c }))
    ];
  }, [affiliations]);

  // Table columns: ID → Name → Category → Code → Actions
  const columns = useMemo(() => [
    { accessorKey: "id", header: "ID", cell: ({ getValue }) => `${getValue()}`.slice(0, 10) + "...", meta: { headerProps: { className: "text-900" } } },
    { accessorFn: (row) => row.displayName || "-", id: "displayName", header: "Affiliation Name", meta: { headerProps: { className: "text-900" } } },
    { accessorFn: (row) => row.category || "-", id: "category", header: "Category", meta: { headerProps: { className: "text-900" } } },
    { accessorKey: "code", header: "Code", meta: { headerProps: { className: "text-900" } } },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <Dropdown className="action-dropdown p-0">
            <Dropdown.Toggle as="button" className="bg-transparent border-0 p-0 shadow-none text-secondary">⋯</Dropdown.Toggle>
            <Dropdown.Menu className="action-menu">
              <Dropdown.Item onClick={() => editAffiliation(item.id)}>Edit</Dropdown.Item>
              <Dropdown.Item onClick={() => viewAffiliation(item.id)}>View</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item className="text-danger" onClick={() => confirmRemove(item.id)}>Delete</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        );
      }
    },
  ], []);

  const table = useAdvanceTable({
    data: filteredAffiliations,
    columns,
    sortable: true,
    perPage: 10,
    pagination: true,
  });

  if (loading && (!affiliations || affiliations.length === 0)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 240 }}>
        <Spinner animation="border" variant="success" />
      </div>
    );
  }

  return (
    <>
      {/* ================= ADD BUTTON ABOVE ================= */}
      <Row className="mb-3">
        <Col className="d-flex justify-content-end">
          <IconButton
            variant="falcon-default"
            size="sm"
            icon="plus"
            transform="shrink-3"
            onClick={() => setShowAssignModal(true)}
            style={{
              backgroundColor: "#10b981",
              color: "#fff",
              borderColor: "#10b981",
              fontWeight: 600,
            }}
          >
            <span className="d-none d-sm-inline-block ms-1">Add Affiliation</span>
          </IconButton>
        </Col>
      </Row>

      {/* ================= SEARCH + CATEGORY FILTER ON ONE LINE ================= */}
      <Row className="mb-2 g-3 align-items-center">
        <Col md={8} lg={9}>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Input
              prefix={<span style={{ fontSize: 14 }}>🔍</span>}
              placeholder="Search by name, code or affiliation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              size="large"
              style={{ borderRadius: 12, height: 30, flex: "1 1 300px", maxWidth: 300 }}
            />
            <Select
              size="large"
              value={selectedCategory}
              onChange={setSelectedCategory}
              options={categoryOptions}
              style={{ minWidth: 180, height: 28 }}
            />
          </div>
        </Col>
        <Col md={4} lg={3} className="text-end">
          <div style={{ fontSize: 13, color: "#64748b" }}>
            Results: <span className="fw-semibold text-dark">{filteredAffiliations.length}</span> of <span className="fw-semibold text-dark">{affiliations.length}</span>
          </div>
        </Col>
      </Row>

      {/* ================= TABLE ================= */}
      <AdvanceTableProvider {...table}>
        <AdvanceTable
          headerClassName="bg-light text-nowrap align-middle text-dark"
          rowClassName="align-middle white-space-nowrap"
          tableProps={{ bordered: true, striped: true, className: "fs-10 mb-0 overflow-hidden" }}
        />
        <CustomPaginationFooter
          table={table}
          totalEntries={filteredAffiliations.length}
          loading={loading}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </AdvanceTableProvider>

      {/* ================= MODALS ================= */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-light border-bottom">
          <Modal.Title className="fw-bold">Edit Affiliation</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <EditAffiliationForm onEdited={handleEdit} id={affiliationToEdit} />
        </Modal.Body>
      </Modal>

      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-light border-bottom">
          <Modal.Title className="fw-bold">Affiliation</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <ViewAffiliationForm id={affiliationToView} />
        </Modal.Body>
      </Modal>

      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} centered size="lg">
        <Modal.Header closeButton className="bg-light border-bottom">
          <Modal.Title className="fw-bold">Add Affiliation</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <AffiliationForm onAdded={handleAssignAdded} />
        </Modal.Body>
      </Modal>

      <ConfirmDeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        onConfirm={handleRemove}
        itemName="affiliation"
        message="Are you sure you want to delete this affiliation?"
      />
    </>
  );
};

export default AffiliationAssignmentsTable;